import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { createAccountForEmail } from '../../utils/adminCreateAccount';
import { saveClientNotes } from '../../utils/clients';
import { watchUnreadForAdmin } from '../../utils/messages';
import DashboardTabs, { useDashboardTab } from '../../components/shared/DashboardTabs';
import TabPanels from '../../components/shared/TabPanels';
import MessagesPanel from './MessagesPanel';
import OutstandingPanel from './OutstandingPanel';
import { watchAllInvoices, isOverdue } from '../../utils/invoices';
import TrackingLinksPanel from './TrackingLinksPanel';
import EnquiriesPanel from './EnquiriesPanel';
import OutreachPanel from './OutreachPanel';
import QuotesPanel from './QuotesPanel';
import ClientsPanel from './ClientsPanel';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  page: ds.page,
  container: ds.container,
  header: ds.header,
  headerTitle: ds.headerTitle,
  headerSubtitle: ds.headerSubtitle,
  button: ds.secondaryButton,
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  input: ds.input,
  textarea: ds.textarea,
  hint: ds.hint,
  submit: ds.primaryButton,
  success: ds.success,
  error: ds.error,
};

function CreateAccountCard() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      await createAccountForEmail(email, name.trim());
      // Written separately: notes live in their own admin-only collection, so
      // they can't be read by the client the way their own record can.
      if (notes.trim()) await saveClientNotes(email, notes);
      setResult({ ok: true, message: `Account created. A "set your password" email was sent to ${email}.` });
      setEmail('');
      setName('');
      setNotes('');
    } catch (err) {
      const message = err.code === 'auth/email-already-in-use'
        ? 'An account already exists for that email.'
        : err.message.replace('Firebase: ', '');
      setResult({ ok: false, message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>Create an account</div>
      <p style={styles.description}>This will send a "set your password" email to the user, giving them access to a personal dashboard.</p>
      <form onSubmit={handleSubmit}>
        {/* Required, because it heads their dashboard and appears on every
            quote, invoice and PDF. Left optional it would be skipped, and
            they'd get a generic page and documents addressed to an email. */}
        <input
          style={styles.input}
          type="text"
          placeholder="Business name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <p style={styles.hint}>
          Used on their dashboard, quotes and invoices — write it as they would.
        </p>
        <textarea
          style={styles.textarea}
          placeholder="Private notes about this client (optional) — only we see these"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <input
          style={styles.input}
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button type="submit" style={styles.submit} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create account'}
        </button>
      </form>
      {result && <div style={result.ok ? styles.success : styles.error}>{result.message}</div>}
    </div>
  );
}

export default function AdminScreen() {
  const { user, logOut } = useAuth();
  const [active, setActive] = useDashboardTab('clients');
  const [unread, setUnread] = useState([]);
  // Watched once here and passed down: ClientsPanel and OutstandingPanel both
  // need every invoice, and the tab badge needs the count on any tab.
  const [invoices, setInvoices] = useState([]);

  useEffect(() => watchAllInvoices(setInvoices, () => setInvoices([])), []);

  // Watched once here, not also inside MessagesPanel: the badge needs it on
  // every tab, and the panel needs the same rows for its waiting-client chips,
  // so one listener is passed down rather than two running the same query.
  useEffect(() => watchUnreadForAdmin(
    setUnread,
    () => setUnread([]),
  ), []);

  const overdueCount = invoices.filter(isOverdue).length;

  const tabs = [
    { id: 'clients', label: 'Clients' },
    { id: 'outstanding', label: 'Outstanding', count: overdueCount },
    { id: 'quotes', label: 'Quotes' },
    { id: 'messages', label: 'Messages', count: unread.length },
    { id: 'links', label: 'Links' },
    { id: 'outreach', label: 'Outreach' },
    { id: 'enquiries', label: 'Enquiries' },
    { id: 'accounts', label: 'Accounts' },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <p style={styles.headerTitle}>Admin</p>
            <p style={styles.headerSubtitle}>Signed in as {user?.email}</p>
          </div>
          <button type="button" style={styles.button} onClick={logOut}>
            Log out
          </button>
        </div>

        <DashboardTabs tabs={tabs} active={active} onChange={setActive} />

        <TabPanels
          active={active}
          panels={[
            { id: 'clients', render: () => <ClientsPanel invoices={invoices} /> },
            { id: 'outstanding', render: () => <OutstandingPanel invoices={invoices} /> },
            { id: 'quotes', render: () => <QuotesPanel /> },
            { id: 'messages', render: () => <MessagesPanel unread={unread} /> },
            { id: 'links', render: () => <TrackingLinksPanel /> },
            { id: 'outreach', render: () => <OutreachPanel /> },
            { id: 'enquiries', render: () => <EnquiriesPanel /> },
            { id: 'accounts', render: () => <CreateAccountCard /> },
          ]}
        />
      </div>
    </div>
  );
}
